import React from 'react';
import { Button } from '../ui/button';
import FormattedMessage from '../formatted-message';

interface TimeoutFallbackProps {
    error?: Error & string;
    retryCallback?: () => void;
    timeoutDuration?: number;
}

const TimeoutFallback: React.FC<TimeoutFallbackProps> = ({
    error,
    retryCallback,
    timeoutDuration = 30000,
}) => {
    const isTimeoutError =
        error?.message?.includes('timed out') ||
        error?.message?.includes('timeout') ||
        error?.message?.includes('ECONNABORTED') ||
        error?.message?.includes(`timeout of ${timeoutDuration}ms exceeded`);

    const handleRetry = () => {
        if (retryCallback) {
            retryCallback();
        } else {
            window.location.reload();
        }
    };

    const handleGoHome = () => {
        window.location.replace('/');
    };

    return (
        <div className='flex min-h-screen items-center justify-center bg-background px-4 py-12 sm:px-6 lg:px-8'>
            <div className='w-full max-w-md space-y-8 text-center'>
                <div>
                    <svg
                        className='mx-auto h-16 w-16 text-destructive animate-bounce'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                    </svg>
                    <h2 className='mt-6 text-3xl font-bold tracking-tight'>
                        Connection Timeout
                    </h2>
                    <p className='mt-2 text-sm text-muted-foreground'>
                        {isTimeoutError
                            ? `Unable to connect to the server after ${timeoutDuration / 1000} seconds.`
                            : 'Unable to establish a connection to the server.'}
                    </p>
                    <p className='mt-2 text-xs text-gray-500'>
                        This could be due to database connectivity issues or
                        network problems.
                        <br />
                        Please try again in a few moments.
                    </p>
                </div>

                {error && import.meta.env.MODE === 'development' && (
                    <div className='mt-4 rounded-md bg-red-50 p-4 space-y-4'>
                        <p className='text-red-700'>
                            <FormattedMessage
                                message={error.message || error}
                            />
                        </p>
                        <p className='text-left font-semibold text-xs text-red-700'>
                            {error.stack}
                        </p>
                    </div>
                )}

                <div className='mt-8 space-y-4'>
                    <Button
                        onClick={handleRetry}
                        className='group relative flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'>
                        <svg
                            className='mr-2 h-4 w-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                            />
                        </svg>
                        Try Again
                    </Button>

                    <Button
                        onClick={handleGoHome}
                        variant={'outline'}
                        className='group relative flex w-full justify-center rounded-md border px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'>
                        Go to Homepage
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TimeoutFallback;
